import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";
import Papa from "papaparse";

import { docUrl } from "../lib/docLinks";
import { contentionMessages } from "../lib/variableCascade";
import {
  Badge,
  Button,
  DataTable,
  Drawer,
  Dropzone,
  HelpTip,
  Inline,
  InlineError,
  Page,
  PageHeader,
  RouterLink,
  Select,
  Stack,
  Text,
} from "../ui";
import { VariableValueField } from "./variables/VariableValueField";
import {
  duplicateMappingTargets,
  declaredVariableErrors,
  draftCellForVariable,
  draftCellError,
  enumValues,
  importedDraftRows,
  normalizedVariableKey,
  undeclaredKeys,
  withCanonicalDraftCell,
  type CsvMapping,
  type DraftCell,
  type DraftRow,
  type DeclaredVariable,
  type ImportedCsv,
  type Variable,
} from "./scenarioRowsState";

export interface ScenarioRowsViewProps {
  agentId?: string;
  declaredVariables: DeclaredVariable[];
  draftRows: DraftRow[] | null;
  isImportOpen: boolean;
  isSavePending: boolean;
  loadedRows: DraftRow[];
  onCancel: () => void;
  onDraftRowsChange: (rows: DraftRow[]) => void;
  onImportOpenChange: (isOpen: boolean) => void;
  onSave: (rows: DraftRow[]) => void;
  loadError?: string;
  isRetrying?: boolean;
  onRetry?: () => void;
  saveError?: string;
  scenarioId?: string;
  variables: Variable[];
}

export function ScenarioRowsView({
  agentId,
  declaredVariables,
  draftRows,
  isImportOpen,
  isSavePending,
  loadedRows,
  loadError,
  isRetrying = false,
  onRetry,
  onCancel,
  onDraftRowsChange,
  onImportOpenChange,
  onSave,
  saveError,
  scenarioId,
  variables,
}: ScenarioRowsViewProps) {
  const [csv, setCsv] = useState<ImportedCsv | null>(null);
  const [mapping, setMapping] = useState<CsvMapping>({});
  const [csvError, setCsvError] = useState<string | null>(null);
  const [ignoredCsvHeaders, setIgnoredCsvHeaders] = useState<string[]>([]);
  const parseGeneration = useRef(0);
  const currentRows = draftRows ?? loadedRows;
  const currentRowsRef = useRef(currentRows);
  currentRowsRef.current = currentRows;
  const undeclaredColumnKeys = useMemo(
    () => undeclaredKeys(currentRows, variables),
    [currentRows, variables],
  );
  const duplicateTargets = useMemo(
    () => duplicateMappingTargets(mapping),
    [mapping],
  );
  const isDirty = draftRows !== null;
  const declarations = declaredVariables;
  const conflictErrors = declaredVariableErrors(declarations);
  const renderingVariables = declarations.map((entry) => entry.variable);
  const wasImportOpen = useRef(isImportOpen);

  useEffect(() => {
    if (draftRows === null) {
      setIgnoredCsvHeaders([]);
    }
  }, [draftRows]);

  useEffect(() => {
    if (wasImportOpen.current && !isImportOpen) {
      parseGeneration.current += 1;
      setCsv(null);
      setCsvError(null);
      setMapping({});
    }
    wasImportOpen.current = isImportOpen;
  }, [isImportOpen]);

  const setCell = useCallback(
    (rowId: string, key: string, cell: DraftCell) => {
      onDraftRowsChange(
        currentRowsRef.current.map((draft) =>
          draft.id === rowId
            ? {
                ...draft,
                value: withCanonicalDraftCell(draft.value, key, cell),
              }
            : draft,
        ),
      );
    },
    [onDraftRowsChange],
  );

  const columns = useMemo<Array<ColumnDef<DraftRow>>>(() => {
    const variableColumns = declarations.map((entry) => ({
      header: () => (
        <Stack gap={1}>
          <Text>{entry.variable.key}</Text>
          {entry.declarations.length > 1 ? (
            <Inline align="center" gap={2}>
              <Badge>Contended</Badge>
              <Text variant="muted">
                {contentionMessages(entry.declarations).join(" ")}
              </Text>
            </Inline>
          ) : null}
        </Stack>
      ),
      id: entry.variable.id,
      cell: ({ row }: { row: { original: DraftRow } }) => {
        const variable = entry.variable;
        const cell = draftCellForVariable(row.original.value, variable.key) ?? {
          kind: "unset",
        };
        const cellValidationError = draftCellError(cell, variable);
        let allowedValues: string[] = [];
        let enumError: string | null = null;
        if (variable.current_revision.value_type === "enum") {
          try {
            allowedValues = enumValues(variable);
          } catch (error) {
            enumError = error instanceof Error ? error.message : String(error);
          }
        }
        const columnDisabled = isSavePending || entry.conflict !== "none";
        return (
          <Inline gap={2}>
            <Select
              disabled={columnDisabled}
              label={`${variable.key} value state`}
              value={cell.kind}
              onChange={(event) => {
                const kind = event.currentTarget.value;
                setCell(
                  row.original.id,
                  variable.key,
                  kind === "value"
                    ? {
                        kind: "value",
                        text:
                          variable.current_revision.value_type === "enum"
                            ? (allowedValues[0] ?? "")
                            : variable.current_revision.value_type === "bool"
                              ? "true"
                              : "",
                      }
                    : kind === "null"
                      ? { kind: "null" }
                      : { kind: "unset" },
                );
              }}
            >
              <option value="unset">Inherit</option>
              <option disabled={enumError !== null} value="value">
                Value
              </option>
              <option value="null">Null (no value)</option>
            </Select>
            {cell.kind === "value"
              ? (() => {
                  const valueType = variable.current_revision.value_type;
                  const input = (
                    <VariableValueField
                      allowedValues={allowedValues}
                      disabled={columnDisabled || enumError !== null}
                      label={variable.key}
                      value={cell.text}
                      valueType={valueType}
                      onChange={(text) => {
                        setCell(row.original.id, variable.key, {
                          kind: "value",
                          text,
                        });
                      }}
                    />
                  );
                  return (
                    <Inline className="marker-scenario-row-cell-value" gap={1}>
                      {entry.isSensitive ? (
                        <Text as="span" variant="masked">
                          {input}
                        </Text>
                      ) : (
                        input
                      )}
                      {(enumError ?? cellValidationError) ? (
                        <InlineError className="marker-scenario-row-cell-error">
                          {enumError ?? cellValidationError}
                        </InlineError>
                      ) : null}
                    </Inline>
                  );
                })()
              : null}
          </Inline>
        );
      },
    }));
    return [
      { header: "Order", id: "order", cell: ({ row }) => row.index + 1 },
      ...variableColumns,
      {
        header: "Action",
        id: "action",
        cell: ({ row }) => (
          <Button
            disabled={isSavePending}
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => {
              onDraftRowsChange(
                currentRowsRef.current.filter(
                  (draft) => draft.id !== row.original.id,
                ),
              );
            }}
          >
            Remove
          </Button>
        ),
      },
    ];
  }, [declarations, isSavePending, onDraftRowsChange, setCell]);

  const closeImport = () => {
    if (isSavePending) return;
    parseGeneration.current += 1;
    setCsv(null);
    setCsvError(null);
    setMapping({});
    onImportOpenChange(false);
  };

  const failCsvParse = (generation: number, message: string) => {
    if (generation !== parseGeneration.current) return;
    setCsv(null);
    setMapping({});
    setCsvError(message);
  };

  const parseCsv = (file: File | null) => {
    const generation = ++parseGeneration.current;
    setCsv(null);
    setMapping({});
    setCsvError(null);
    if (file === null || isSavePending) return;
    Papa.parse<string[]>(file, {
      complete: (result) => {
        if (generation !== parseGeneration.current) return;
        if (result.errors.length > 0 || result.data.length === 0) {
          failCsvParse(
            generation,
            result.errors[0]?.message ?? "CSV needs a header row.",
          );
          return;
        }
        const [headers = [], ...rows] = result.data;
        const columns = headers.map((header, index) => ({
          header,
          id: `column-${String(index)}`,
          index,
          isUnnamed: header.length === 0,
          label:
            header.length === 0
              ? `Unnamed column ${String(index + 1)}`
              : header,
        }));
        setCsv({
          columns,
          rows,
        });
        const variablesByKey = new Map(
          renderingVariables.map((variable) => [
            normalizedVariableKey(variable.key),
            variable.id,
          ]),
        );
        setMapping(
          Object.fromEntries(
            columns.map((column) => {
              const variableId = variablesByKey.get(
                normalizedVariableKey(column.header),
              );
              return [
                column.id,
                column.isUnnamed || variableId === undefined
                  ? { kind: "ignore" }
                  : { kind: "variable", variableId },
              ];
            }),
          ),
        );
      },
      error: (error) => {
        failCsvParse(generation, error.message);
      },
      delimiter: ",",
      header: false,
      skipEmptyLines: true,
    });
  };

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          <>
            {conflictErrors.map((message) => (
              <InlineError key={message}>{message}</InlineError>
            ))}
            {saveError ? <InlineError>{saveError}</InlineError> : null}
            <Button
              disabled={isSavePending}
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => {
                onDraftRowsChange([
                  ...currentRows,
                  { id: window.crypto.randomUUID(), value: {} },
                ]);
              }}
            >
              Add scenario row
            </Button>
            <Button
              disabled={isSavePending}
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => {
                onImportOpenChange(true);
              }}
            >
              Import CSV
            </Button>
            <Button
              disabled={isSavePending}
              size="sm"
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              disabled={
                isSavePending ||
                !isDirty ||
                conflictErrors.length > 0 ||
                loadError !== undefined
              }
              size="sm"
              type="button"
              onClick={() => {
                onSave(currentRows);
              }}
            >
              Save scenario rows
            </Button>
          </>
        }
        help={
          <HelpTip
            docHref={docUrl("guides/use-variables-in-simulations")}
            docLabel="Supply Scenario row values"
            label="About Scenario rows"
          >
            {
              "Each row adds a variation for every selected Persona. Inherit leaves the Agent, Persona, or Scenario value in place. Value supplies a row override; Null explicitly clears it and can block required inputs or text tokens. Launch overrides take precedence. CSV import replaces the table when you save."
            }
          </HelpTip>
        }
        title="Scenario rows"
      />
      {renderingVariables.length === 0 && agentId ? (
        <Inline gap={2}>
          <Text variant="muted">Declare a variable to add a column.</Text>
          <RouterLink
            params={{ agentId }}
            rel="noopener"
            search={{
              new: 1,
              scope_type: scenarioId ? "scenario" : undefined,
              scope_id: scenarioId,
            }}
            target="_blank"
            to="/agents/$agentId/variables"
          >
            Declare variable
          </RouterLink>
        </Inline>
      ) : null}
      {undeclaredColumnKeys.length > 0 ? (
        <Stack aria-live="polite" gap={2} role="status">
          {undeclaredColumnKeys.map((key) => (
            <Inline gap={2} key={key}>
              <Badge tone="warning">Warning</Badge>
              <Text>{key} is undeclared and ignored.</Text>
              {agentId ? (
                <RouterLink
                  params={{ agentId }}
                  rel="noopener"
                  search={{
                    key,
                    new: 1,
                    scope_type: scenarioId ? "scenario" : undefined,
                    scope_id: scenarioId,
                  }}
                  target="_blank"
                  to="/agents/$agentId/variables"
                >
                  Declare {key}
                </RouterLink>
              ) : null}
            </Inline>
          ))}
        </Stack>
      ) : null}
      {ignoredCsvHeaders.length > 0 ? (
        <Inline aria-live="polite" gap={2} role="status">
          <Badge tone="warning">Warning</Badge>
          <Text>Ignored CSV headers: {ignoredCsvHeaders.join(", ")}</Text>
        </Inline>
      ) : null}
      {loadError ? (
        <Inline gap={2}>
          <InlineError>{loadError}</InlineError>
          {onRetry === undefined ? null : (
            <Button
              disabled={isRetrying}
              type="button"
              variant="secondary"
              size="sm"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
        </Inline>
      ) : null}
      <DataTable
        ariaLabel="Scenario rows"
        columns={columns}
        data={currentRows}
        getRowId={(row) => row.id}
        virtual={{ fill: true, interactiveRows: true, rowHeight: 40 }}
      />
      <Drawer
        ariaLabel="Import scenario rows"
        isOpen={isImportOpen}
        onDismiss={closeImport}
        size="lg"
      >
        <Stack gap={4}>
          <Dropzone
            accept=".csv,text/csv"
            disabled={isSavePending}
            id="scenario-rows-csv"
            label="CSV file"
            onFileChange={parseCsv}
          />
          {csvError ? <InlineError>{csvError}</InlineError> : null}
          {csv ? (
            <>
              <Text variant="muted">
                Map columns to variables; ignored columns are omitted.
              </Text>
              {csv.columns.map((column) => {
                const target = mapping[column.id];
                if (column.isUnnamed) {
                  return (
                    <Inline gap={2} key={column.id}>
                      <Text>{column.label}</Text>
                      <Badge tone="warning">Ignored</Badge>
                    </Inline>
                  );
                }
                return (
                  <Select
                    key={column.id}
                    disabled={isSavePending}
                    label={column.label}
                    value={target?.kind === "variable" ? target.variableId : ""}
                    onChange={(event) => {
                      setMapping((previous) => ({
                        ...previous,
                        [column.id]: event.currentTarget.value
                          ? {
                              kind: "variable",
                              variableId: event.currentTarget.value,
                            }
                          : { kind: "ignore" },
                      }));
                    }}
                  >
                    <option value="">Ignore</option>
                    {renderingVariables.map((variable) => {
                      const selectedElsewhere = Object.entries(mapping).some(
                        ([otherColumnId, target]) =>
                          otherColumnId !== column.id &&
                          target.kind === "variable" &&
                          target.variableId === variable.id,
                      );
                      return (
                        <option
                          key={variable.id}
                          disabled={selectedElsewhere}
                          value={variable.id}
                        >
                          {variable.key}
                        </option>
                      );
                    })}
                  </Select>
                );
              })}
              {duplicateTargets.size > 0 ? (
                <InlineError>
                  Each variable can be mapped from only one CSV column.
                </InlineError>
              ) : null}
              <Inline justify="end">
                <Button
                  disabled={isSavePending}
                  type="button"
                  variant="secondary"
                  onClick={closeImport}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isSavePending || duplicateTargets.size > 0}
                  type="button"
                  onClick={() => {
                    setIgnoredCsvHeaders(
                      csv.columns
                        .filter(
                          (column) => mapping[column.id]?.kind !== "variable",
                        )
                        .map((column) => column.label),
                    );
                    onDraftRowsChange(
                      importedDraftRows(csv, mapping, variables),
                    );
                    closeImport();
                  }}
                >
                  Import rows
                </Button>
              </Inline>
            </>
          ) : null}
        </Stack>
      </Drawer>
    </Page>
  );
}
